import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { message, Modal, Input } from "antd";
import { VaultFile, VaultFolder, ViewMode, SortField, SortOrder } from "@/types";
import { useFiles, useFolders, useBreadcrumb, useUpload, useSelection } from "@/hooks";
import { ROUTES } from "@/utils/constants";
import { EmptyState } from "@/components/shared";
import { downloadFile, createFolder, deleteFile, deleteFolder } from "@/services/storageManager";
import Breadcrumb from "./Breadcrumb";
import Toolbar from "./Toolbar";
import FolderGrid from "./FolderGrid";
import FolderTable from "./FolderTable";
import FileGrid from "./FileGrid";
import FileTable from "./FileTable";
import DetailPanel from "./DetailPanel";
import FolderDetailPanel from "./FolderDetailPanel";
import UploadZone from "./UploadZone";
import UploadProgress from "./UploadProgress";
import ShareModal from "./ShareModal";
import ShareFolderModal from "./ShareFolderModal";
import PreviewModal from "./PreviewModal";
import NewFolderModal from "./NewFolderModal";
import BulkActions from "./BulkActions";

interface FileExplorerProps {
  folderId: string | null;
}

export default function FileExplorer({ folderId }: FileExplorerProps) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("vault-view-mode") as ViewMode) || "grid";
    }
    return "grid";
  });
  const [sortBy, setSortBy] = useState<SortField>("date");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [searchQuery, setSearchQuery] = useState("");

  // Selection state
  const selection = useSelection();

  // Detail panel state
  const [detailFile, setDetailFile] = useState<VaultFile | null>(null);
  const [detailFolder, setDetailFolder] = useState<VaultFolder | null>(null);

  // Modal states
  const [shareFile, setShareFile] = useState<VaultFile | null>(null);
  const [shareFolder, setShareFolder] = useState<VaultFolder | null>(null);
  const [previewFile, setPreviewFile] = useState<VaultFile | null>(null);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [renameItem, setRenameItem] = useState<{
    type: "file" | "folder";
    id: string;
    name: string;
  } | null>(null);

  // Data fetching
  const { folders, isLoading: foldersLoading, mutate: mutateFolders } = useFolders(folderId);
  const { files, isLoading: filesLoading, mutate: mutateFiles } = useFiles({
    folderId,
    search: searchQuery,
    sort: sortBy,
    order: sortOrder,
  });
  const { breadcrumb } = useBreadcrumb(folderId);

  // Upload handling
  const { uploads, addFiles, cancelUpload, clearUploads } = useUpload(folderId, () => {
    mutateFiles();
  });

  // View mode change handler
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    if (typeof window !== "undefined") {
      localStorage.setItem("vault-view-mode", mode);
    }
  }, []);

  // Sort change handler
  const handleSortChange = useCallback((field: SortField, order: SortOrder) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  // Navigation handlers
  const handleFolderDoubleClick = useCallback(
    (folder: VaultFolder) => {
      navigate(ROUTES.folder(folder.id));
    },
    [navigate]
  );

  const handleFolderClick = useCallback(
    (folder: VaultFolder, e: React.MouseEvent) => {
      const items = [
        ...folders.map((f) => ({ id: f.id, type: "folder" as const })),
        ...files.map((f) => ({ id: f.id, type: "file" as const })),
      ];
      selection.handleClick(folder.id, items, e);
      setDetailFolder(folder);
      setDetailFile(null);
    },
    [folders, files, selection]
  );

  const handleFileClick = useCallback(
    (file: VaultFile, e: React.MouseEvent) => {
      const items = [
        ...folders.map((f) => ({ id: f.id, type: "folder" as const })),
        ...files.map((f) => ({ id: f.id, type: "file" as const })),
      ];
      selection.handleClick(file.id, items, e);
      setDetailFile(file);
      setDetailFolder(null);
    },
    [folders, files, selection]
  );

  const handleFileDoubleClick = useCallback((file: VaultFile) => {
    setPreviewFile(file);
  }, []);

  // Upload handlers
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files);
        e.target.value = "";
      }
    },
    [addFiles]
  );

  // File actions
  const handleDownload = useCallback(async (file: VaultFile) => {
    try {
      await downloadFile(file.id, file.fileName);
      message.success(`Downloaded ${file.fileName}`);
    } catch (error: any) {
      console.error("Download error:", error);
      message.error(error.response?.data?.message || "Failed to download file");
    }
  }, []);

  const handleDelete = useCallback(
    (file: VaultFile) => {
      Modal.confirm({
        title: "Delete file?",
        content: `Are you sure you want to delete "${file.fileName}"? This action cannot be undone.`,
        okText: "Delete",
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await deleteFile(file.id);
            message.success(`"${file.fileName}" deleted`);
            mutateFiles();
          } catch (error) {
            console.error("Failed to delete file:", error);
            message.error("Failed to delete file");
          }
        },
      });
    },
    [mutateFiles]
  );

  const handleRename = useCallback((file: VaultFile) => {
    setRenameItem({ type: "file", id: file.id, name: file.fileName });
  }, []);

  // Folder actions
  const handleFolderContextMenu = useCallback(
    (_folder: VaultFolder, e: React.MouseEvent) => {
      e.preventDefault();
    },
    []
  );

  const handleFolderShare = useCallback((folder: VaultFolder) => {
    setShareFolder(folder);
  }, []);

  const handleFolderRename = useCallback((folder: VaultFolder) => {
    setRenameItem({ type: "folder", id: folder.id, name: folder.name });
  }, []);

  const handleFolderDelete = useCallback(
    (folder: VaultFolder) => {
      Modal.confirm({
        title: "Delete folder?",
        content: `Are you sure you want to delete "${folder.name}" and all its contents? This action cannot be undone.`,
        okText: "Delete",
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await deleteFolder(folder.id);
            message.success(`"${folder.name}" deleted`);
            mutateFolders();
            mutateFiles(); // Also refresh files in case folder had files
          } catch (error) {
            console.error("Failed to delete folder:", error);
            message.error("Failed to delete folder");
          }
        },
      });
    },
    [mutateFolders, mutateFiles]
  );

  const handleFileContextMenu = useCallback(
    (_file: VaultFile, e: React.MouseEvent) => {
      e.preventDefault();
    },
    []
  );

  const handleCreateFolder = useCallback(
    async (name: string, color: string, icon: string) => {
      try {
        await createFolder({
          name,
          parentFolderId: folderId,
          color,
          icon,
        });
        message.success(`Folder "${name}" created`);
        mutateFolders();
        setShowNewFolderModal(false);
      } catch (error: any) {
        message.error(error.response?.data?.message || "Failed to create folder");
      }
    },
    [folderId, mutateFolders]
  );

  const handleRenameConfirm = useCallback(
    async (newName: string) => {
      if (!renameItem) return;
      // In real implementation, call API to rename
      message.success(`Renamed to "${newName}"`);
      if (renameItem.type === "folder") {
        mutateFolders();
      } else {
        mutateFiles();
      }
      setRenameItem(null);
    },
    [renameItem, mutateFolders, mutateFiles]
  );

  // Bulk actions
  const handleBulkDownload = useCallback(() => {
    message.info("Downloading selected items...");
  }, []);

  const handleBulkShare = useCallback(() => {
    message.info("Share functionality for multiple items coming soon");
  }, []);

  const handleBulkDelete = useCallback(() => {
    Modal.confirm({
      title: "Delete selected items?",
      content: `Are you sure you want to delete ${selection.selectedCount} items? This action cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await Promise.all(
            Array.from(selection.selectedIds).map((id: string) => deleteFile(id))
          );
          message.success(`${selection.selectedCount} items deleted`);
          selection.clearSelection();
          mutateFiles();
          mutateFolders();
        } catch (error) {
          console.error("Failed to delete items:", error);
          message.error("Failed to delete some items");
        }
      },
    });
  }, [selection, mutateFiles, mutateFolders]);

  const isLoading = foldersLoading || filesLoading;
  const isEmpty = !isLoading && folders.length === 0 && files.length === 0;
  const totalItems = folders.length + files.length;

  return (
    <div style={{ display: "flex", gap: 16, height: "100%" }}>
      {/* Main content area */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <UploadZone onFilesDropped={addFiles}>
          {/* Breadcrumb */}
          <Breadcrumb
            folders={breadcrumb}
            itemCount={totalItems}
            isLoading={isLoading}
          />

          {/* Toolbar */}
          <Toolbar
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onUploadClick={handleUploadClick}
            onNewFolderClick={() => setShowNewFolderModal(true)}
          />

          {/* Bulk actions */}
          <BulkActions
            selectedCount={selection.selectedCount}
            onDownload={handleBulkDownload}
            onShare={handleBulkShare}
            onDelete={handleBulkDelete}
            onClearSelection={selection.clearSelection}
          />

          {/* Content */}
          {isEmpty ? (
            <EmptyState
              type={searchQuery ? "search" : "folder"}
              actionLabel={searchQuery ? undefined : "Upload files"}
              onAction={searchQuery ? undefined : handleUploadClick}
            />
          ) : (
            <>
              {/* Folders - Grid View */}
              {folders.length > 0 && viewMode === "grid" && (
                <FolderGrid
                  folders={folders}
                  isLoading={foldersLoading}
                  selectedIds={selection.selectedIds}
                  onFolderClick={handleFolderClick}
                  onFolderDoubleClick={handleFolderDoubleClick}
                  onFolderContextMenu={handleFolderContextMenu}
                  onFolderShare={handleFolderShare}
                  onFolderRename={handleFolderRename}
                  onFolderDelete={handleFolderDelete}
                />
              )}

              {/* Folders - List View */}
              {folders.length > 0 && viewMode === "list" && (
                <FolderTable
                  folders={folders}
                  isLoading={foldersLoading}
                  selectedIds={selection.selectedIds}
                  onFolderClick={handleFolderClick}
                  onFolderDoubleClick={handleFolderDoubleClick}
                  onFolderShare={handleFolderShare}
                  onFolderRename={handleFolderRename}
                  onFolderDelete={handleFolderDelete}
                  onSelectionChange={selection.selectMultiple}
                />
              )}

              {/* Files - Grid View */}
              {files.length > 0 && viewMode === "grid" && (
                <FileGrid
                  files={files}
                  isLoading={filesLoading}
                  selectedIds={selection.selectedIds}
                  onFileClick={handleFileClick}
                  onFileDoubleClick={handleFileDoubleClick}
                  onFileContextMenu={handleFileContextMenu}
                />
              )}

              {/* Files - List View */}
              {files.length > 0 && viewMode === "list" && (
                <FileTable
                  files={files}
                  isLoading={filesLoading}
                  selectedIds={selection.selectedIds}
                  onFileClick={handleFileClick}
                  onFileDoubleClick={handleFileDoubleClick}
                  onDownload={handleDownload}
                  onShare={(file) => setShareFile(file)}
                  onRename={handleRename}
                  onDelete={handleDelete}
                  onSelectionChange={selection.selectMultiple}
                />
              )}
            </>
          )}
        </UploadZone>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />
      </div>

      {/* Detail panel - File */}
      {detailFile && (
        <DetailPanel
          file={detailFile}
          onClose={() => setDetailFile(null)}
          onDownload={() => handleDownload(detailFile)}
          onShare={() => setShareFile(detailFile)}
        />
      )}

      {/* Detail panel - Folder */}
      {detailFolder && (
        <FolderDetailPanel
          folder={detailFolder}
          onClose={() => setDetailFolder(null)}
          onOpen={() => handleFolderDoubleClick(detailFolder)}
          onShare={() => setShareFolder(detailFolder)}
        />
      )}

      {/* Upload progress */}
      <UploadProgress
        uploads={uploads}
        onCancel={cancelUpload}
        onClearAll={clearUploads}
      />

      {/* Modals */}
      {shareFile && (
        <ShareModal
          file={shareFile}
          open={!!shareFile}
          onClose={() => setShareFile(null)}
        />
      )}

      {shareFolder && (
        <ShareFolderModal
          folder={shareFolder}
          open={!!shareFolder}
          onClose={() => setShareFolder(null)}
        />
      )}

      <PreviewModal
        file={previewFile}
        files={files}
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
        onDownload={handleDownload}
        onShare={(file) => setShareFile(file)}
        onNavigate={setPreviewFile}
      />

      <NewFolderModal
        open={showNewFolderModal}
        parentFolderId={folderId}
        onClose={() => setShowNewFolderModal(false)}
        onCreate={handleCreateFolder}
      />

      {/* Rename modal */}
      <Modal
        title={`Rename ${renameItem?.type}`}
        open={!!renameItem}
        onCancel={() => setRenameItem(null)}
        onOk={() => {
          const input = document.getElementById("rename-input") as HTMLInputElement;
          if (input && input.value) {
            handleRenameConfirm(input.value);
          }
        }}
      >
        <Input
          id="rename-input"
          defaultValue={renameItem?.name}
          autoFocus
        />
      </Modal>
    </div>
  );
}
